import { Capacitor } from "@capacitor/core";
import { DriverCredentialVault } from "./credentialPlugin";

const WEB_PHONE_KEY = "driver_remembered_phone";
const WEB_PASSWORD_KEY = "driver_remembered_password";
const BIOMETRIC_LOGIN_ENABLED_KEY = "driver_biometric_login_enabled";
const NATIVE_PLUGIN_TIMEOUT_MS = 12000;

function withNativeTimeout(promise, operation) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${operation}_timeout`));
    }, NATIVE_PLUGIN_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => {
    window.clearTimeout(timeoutId);
  });
}

function readWebValue(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeWebValue(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in restricted WebView/private contexts.
  }
}

function removeWebValue(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures in restricted WebView/private contexts.
  }
}

function clearLegacyPasswordStorage() {
  if (!Capacitor.isNativePlatform()) return;
  removeWebValue(WEB_PASSWORD_KEY);
}

export function getInitialRememberedPhone() {
  return readWebValue(WEB_PHONE_KEY);
}

export function hasRememberedCredentialsHint() {
  return Boolean(readWebValue(WEB_PHONE_KEY) || readWebValue(WEB_PASSWORD_KEY));
}

export function isDriverBiometricLoginEnabled() {
  return readWebValue(BIOMETRIC_LOGIN_ENABLED_KEY) === "true";
}

export function setDriverBiometricLoginEnabled(enabled) {
  if (enabled) {
    writeWebValue(BIOMETRIC_LOGIN_ENABLED_KEY, "true");
  } else {
    removeWebValue(BIOMETRIC_LOGIN_ENABLED_KEY);
  }
  return { enabled: Boolean(enabled) };
}

export async function isDriverBiometricAvailable() {
  if (!Capacitor.isNativePlatform()) {
    return { available: false, reason: "native_only" };
  }

  try {
    const result = await withNativeTimeout(
      DriverCredentialVault.isBiometricAvailable(),
      "biometric_availability"
    );
    return {
      available: result?.available === true,
      biometryType: result?.biometryType || "biometric",
      reason: result?.reason || "",
    };
  } catch (error) {
    console.warn("[DriverCredentials] Biometric availability check failed:", error);
    return { available: false, reason: "biometric_check_failed" };
  }
}

export async function authenticateDriverBiometric(reason) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("biometric_native_only");
  }

  const result = await withNativeTimeout(
    DriverCredentialVault.authenticate({
      reason: reason || "Authenticate to sign in to Big Taxi Driver",
    }),
    "biometric_authentication"
  );
  if (result?.authenticated !== true) {
    throw new Error("biometric_authentication_failed");
  }
  return result;
}

export async function loadBiometricDriverCredentials(reason) {
  if (!isDriverBiometricLoginEnabled()) {
    throw new Error("biometric_login_disabled");
  }

  await authenticateDriverBiometric(reason);
  const credentials = await loadRememberedDriverCredentials();
  if (!credentials.phone || !credentials.password) {
    throw new Error("biometric_credentials_missing");
  }
  return credentials;
}

export async function loadRememberedDriverCredentials() {
  if (!Capacitor.isNativePlatform()) {
    return {
      phone: readWebValue(WEB_PHONE_KEY),
      password: readWebValue(WEB_PASSWORD_KEY),
      source: "web_storage",
    };
  }

  try {
    const credentials = await withNativeTimeout(DriverCredentialVault.getCredentials(), "credential_load");
    const phone = credentials?.phone || readWebValue(WEB_PHONE_KEY);
    let password = credentials?.password || "";

    if (!password) {
      const legacyPassword = readWebValue(WEB_PASSWORD_KEY);
      if (legacyPassword) {
        password = legacyPassword;
        await saveRememberedDriverCredentials({
          phone,
          password,
        });
      }
    }

    clearLegacyPasswordStorage();
    return {
      phone,
      password,
      source: "keychain",
    };
  } catch (error) {
    console.warn("[DriverCredentials] Keychain load failed:", error);
    clearLegacyPasswordStorage();
    return {
      phone: readWebValue(WEB_PHONE_KEY),
      password: "",
      source: "keychain_load_failed",
    };
  }
}

export async function saveRememberedDriverCredentials({ phone, password }) {
  if (!Capacitor.isNativePlatform()) {
    writeWebValue(WEB_PHONE_KEY, phone || "");
    writeWebValue(WEB_PASSWORD_KEY, password || "");
    return { status: "saved_web_storage" };
  }

  writeWebValue(WEB_PHONE_KEY, phone || "");

  try {
    await withNativeTimeout(
      DriverCredentialVault.saveCredentials({
        phone: phone || "",
        password: password || "",
      }),
      "credential_save"
    );
    clearLegacyPasswordStorage();
    return { status: "saved_keychain" };
  } catch (error) {
    console.warn("[DriverCredentials] Keychain save failed:", error);
    clearLegacyPasswordStorage();
    return { status: "keychain_save_failed" };
  }
}

export async function clearRememberedDriverCredentials() {
  if (Capacitor.isNativePlatform()) {
    try {
      await withNativeTimeout(DriverCredentialVault.clearCredentials(), "credential_clear");
    } catch (error) {
      console.warn("[DriverCredentials] Keychain clear failed:", error);
    }
  }
  removeWebValue(WEB_PHONE_KEY);
  removeWebValue(WEB_PASSWORD_KEY);
  removeWebValue(BIOMETRIC_LOGIN_ENABLED_KEY);
  return { status: "cleared" };
}
